import { useEffect, useState } from 'react';
import { Link } from 'wouter';
import { ArrowUpRight, CreditCard, ExternalLink, Link2, Link2Off, Search, User } from 'lucide-react';
import { AdminAPI } from '@shared/types/api';
import { useToast } from '@/components/ux/Toast';
import { useLinkTriageUser, useTriageUserSearch } from '../../api/useAdminTriage';
import { useAdminUserNotes } from '../../api/useAdminUsers';
import { UserNotesSection } from '../UserNotesSection';
import { timeAgo } from '../ops/opsUi';
import { stripeCustomerUrl } from '../../utils/stripeLinks';
import { TicketStatusChip, TriageAvatar } from './triageUi';

// The "who is this" column: the matched Outclose user with their booking
// context and past tickets, or (when unmatched) the fuzzy candidates plus a
// manual search, each with a one-click Link.

function CandidateRow({
  candidate,
  onLink,
  linking,
}: {
  candidate: AdminAPI.TriageMatchCandidate;
  onLink: (userId: string) => void;
  linking: boolean;
}) {
  const name = [candidate.firstName, candidate.lastName].filter(Boolean).join(' ');
  return (
    <div className="flex items-center gap-2 py-1.5">
      <TriageAvatar name={name} imageUrl={candidate.profileImageUrl} size={6} />
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium truncate">
          <Link
            href={`/users?q=${encodeURIComponent(candidate.userId)}`}
            title="Open in Users"
            className="hover:underline"
          >
            {name}
          </Link>
          {candidate.city && <span className="text-dark-muted font-normal"> · {candidate.city}</span>}
        </p>
        <p className="text-[11px] text-dark-muted truncate">
          {candidate.email ?? candidate.username} · {candidate.method} {candidate.score}
        </p>
      </div>
      <button
        onClick={() => onLink(candidate.userId)}
        disabled={linking}
        className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-neutral-300 dark:border-neutral-600 text-xs font-medium hover:border-primary-400 hover:text-primary-500 disabled:opacity-50"
      >
        <Link2 className="w-3 h-3" />
        Link
      </button>
    </div>
  );
}

function HistoryItem({ item }: { item: AdminAPI.TriageHistoryItem }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="py-1.5 border-t border-neutral-100 dark:border-neutral-800 first:border-t-0">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left flex items-center gap-2"
      >
        <span className="text-xs truncate flex-1">{item.title ?? item.category ?? 'Ticket'}</span>
        <TicketStatusChip status={item.status} />
        <span className="text-[11px] text-dark-muted whitespace-nowrap">
          {timeAgo(item.lastInboundAt ?? item.createdAt)}
        </span>
      </button>
      {expanded && (
        <div className="mt-1 space-y-1">
          {item.summary && <p className="text-xs text-dark-muted">{item.summary}</p>}
          <Link href={`/triage/${item.id}`} className="text-[11px] text-primary-500 hover:underline">
            Open ticket
          </Link>
        </div>
      )}
    </div>
  );
}

export default function TriageUserCard({
  ticket,
  matchedUser,
  matchCandidates,
  history,
}: {
  ticket: AdminAPI.TriageTicketSummary;
  matchedUser: AdminAPI.TriageMatchedUserCard | null;
  matchCandidates: AdminAPI.TriageMatchCandidate[];
  history: AdminAPI.TriageHistoryItem[];
}) {
  const toast = useToast();
  const linkUser = useLinkTriageUser();

  // Debounced manual search for the unmatched case.
  const [searchInput, setSearchInput] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);
  const { data: searchData, isFetching: searching } = useTriageUserSearch(debouncedQuery);
  const searchResults = searchData?.data?.candidates ?? [];

  const doLink = (userId: string | null) => {
    linkUser.mutate(
      { ticketId: ticket.id, userId },
      {
        onSuccess: () => toast.success(userId ? 'User linked' : 'User unlinked'),
        onError: (err) => toast.error(err instanceof Error ? err.message : 'Link failed'),
      }
    );
  };

  const pastTickets = history.filter((h) => h.id !== ticket.id);
  const { data: notesData } = useAdminUserNotes(matchedUser?.userId ?? null);

  return (
    <div className="rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-3 space-y-3">
      <div className="flex items-center gap-2">
        <User className="w-4 h-4 text-dark-muted" />
        <h3 className="text-sm font-semibold flex-1">User</h3>
        {ticket.matchedUserId && (
          <button
            onClick={() => doLink(null)}
            disabled={linkUser.isPending}
            title="Remove the user link from this ticket"
            className="inline-flex items-center gap-1 text-[11px] text-dark-muted hover:text-red-500 disabled:opacity-50"
          >
            <Link2Off className="w-3 h-3" />
            Unlink
          </button>
        )}
      </div>

      {matchedUser ? (
        <div className="space-y-2">
          <Link
            href={`/users?q=${encodeURIComponent(matchedUser.userId)}`}
            title="Open in Users"
            className="group flex items-center gap-2.5 -m-1 p-1 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-800"
          >
            <TriageAvatar
              name={[matchedUser.firstName, matchedUser.lastName].filter(Boolean).join(' ')}
              imageUrl={matchedUser.profileImageUrl}
              size={10}
            />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate group-hover:underline">
                {[matchedUser.firstName, matchedUser.lastName].filter(Boolean).join(' ')}
                {matchedUser.city && (
                  <span className="text-dark-muted font-normal"> · {matchedUser.city}</span>
                )}
              </p>
              <p className="text-xs text-dark-muted truncate">{matchedUser.email ?? matchedUser.username}</p>
            </div>
            <ArrowUpRight className="w-3.5 h-3.5 text-dark-muted opacity-0 group-hover:opacity-100 shrink-0" />
          </Link>
          <div className="text-xs text-dark-muted space-y-0.5">
            {ticket.matchConfidence && <p>Match confidence: {ticket.matchConfidence}</p>}
            <p className="flex items-center gap-2 flex-wrap">
              <span>
                Subscription:{' '}
                <span className="font-medium text-type-900 dark:text-white">
                  {matchedUser.subscriptionStatus ?? 'none'}
                </span>
              </span>
              {matchedUser.stripeCustomerId && (
                <a
                  href={stripeCustomerUrl(matchedUser.stripeCustomerId)}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Open the customer in the Stripe dashboard"
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-primary-500/15 text-primary-500 font-medium hover:bg-primary-500/25"
                >
                  <CreditCard className="w-3 h-3" />
                  Stripe
                  <ExternalLink className="w-2.5 h-2.5" />
                </a>
              )}
            </p>
            {matchedUser.upcomingBooking && (
              <p>
                Upcoming: {matchedUser.upcomingBooking.date} {matchedUser.upcomingBooking.time}
                {matchedUser.upcomingBooking.neighborhoodName
                  ? ` · ${matchedUser.upcomingBooking.neighborhoodName}`
                  : ''}
              </p>
            )}
            {matchedUser.lastAttended && (
              <p>
                Last attended: {matchedUser.lastAttended.date}
                {matchedUser.lastAttended.neighborhoodName
                  ? ` · ${matchedUser.lastAttended.neighborhoodName}`
                  : ''}
              </p>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-dark-muted">
            No Outclose user linked. {matchCandidates.length > 0 ? 'Best matches:' : ''}
          </p>
          {matchCandidates.length > 0 && (
            <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {matchCandidates.map((candidate) => (
                <CandidateRow
                  key={candidate.userId}
                  candidate={candidate}
                  onLink={doLink}
                  linking={linkUser.isPending}
                />
              ))}
            </div>
          )}
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-dark-muted" />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search users by name or email"
              className="w-full pl-8 pr-3 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-transparent text-xs"
            />
          </div>
          {searching && <p className="text-[11px] text-dark-muted">Searching…</p>}
          {!searching && debouncedQuery.trim().length >= 2 && (
            <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {searchResults.length === 0 && (
                <p className="text-[11px] text-dark-muted py-1">No users found.</p>
              )}
              {searchResults.map((candidate) => (
                <CandidateRow
                  key={candidate.userId}
                  candidate={candidate}
                  onLink={doLink}
                  linking={linkUser.isPending}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {matchedUser && (
        <UserNotesSection userId={matchedUser.userId} notes={notesData?.data ?? []} />
      )}

      <div>
        <h4 className="text-xs font-semibold text-dark-muted mb-1">
          Previous tickets {pastTickets.length > 0 && `(${pastTickets.length})`}
        </h4>
        {pastTickets.length === 0 ? (
          <p className="text-[11px] text-dark-muted">None.</p>
        ) : (
          <div>
            {pastTickets.map((item) => (
              <HistoryItem key={item.id} item={item} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
